import type { ReactNode } from 'react';
import React from 'react';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import TurnRightIcon from '@mui/icons-material/TurnRight';
import Button from '@mui/material/Button/Button';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Portal from '@mui/material/Portal';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { useField } from 'app-components/flux/transaction-rules/transactionRulesFormHooks';
import { useStateContext } from 'app-components/flux/transaction-rules/transactionRulesFormState';
import type { TransactionRulesStages } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import StandardDrawer, { useStandardDrawerVariant } from 'components/Drawers/StandardDrawer';
import { getDrawerSx } from 'components/Drawers/drawerStyles';
import { useAppSelector } from 'hooks/redux';

const RULES_V3_DRAWER_WIDTH = 'calc(100% - 75px)';

const useStyles = makeStyles()((theme) => ({
  drawer: {
    width: 'calc(100% - 75px)',
    flexShrink: 0,
  },
  drawerPaper: {
    maxHeight: '100%',
    width: 'calc(100% - 75px)',
    overflow: 'hidden',
  },
  standardDrawerPaper: {
    overflow: 'hidden',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    flex: 1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    flex: 0,
    justifyContent: 'space-between',
    padding: `${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(2)} ${theme.spacing(3)}`,
    borderBottom: `1px solid ${theme.palette.border.main}`,
    minHeight: theme.spacing(9),
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  headerActionSpacer: {
    width: theme.spacing(5),
    height: theme.spacing(5),
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(1.75),
  },
  instructions: {
    display: 'flex',
    background: theme.palette.background.paper,
    borderRadius: theme.spacing(1.5),
    padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
    boxShadow: theme.shadows[10],
  },
  spacer: {
    width: 140,
    minHeight: theme.spacing(5.625),
  },
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    padding: `${theme.spacing(2)} ${theme.spacing(5)} 0px ${theme.spacing(5)}`,
    backgroundColor: theme.palette.background.default,
    minHeight: 0,
    overflow: 'auto',
  },
  footer: {
    display: 'flex',
    flex: 0,
    justifyContent: 'flex-end',
    padding: `${theme.spacing(2)} ${theme.spacing(4)} ${theme.spacing(3)}`,
    borderTop: `1px solid ${theme.palette.border.main}`,
    minHeight: theme.spacing(10),
  },
  backNav: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    justifyContent: 'flex-start',
  },
  backNavText: {
    fontWeight: 500,
  },
}));

interface CustomDrawerProps {
  id: string;
  isOverlay?: boolean;
  open: boolean;
  stage: TransactionRulesStages;
  onClose: () => void;
  onDismiss?: () => void;
  onExited?: () => void;
  header: ReactNode;
  children: ReactNode;
  footer: ReactNode;
  backText: string;
}

type HeaderElementProps = {
  children?: ReactNode;
};

const getHeaderTitleContent = (header: ReactNode): ReactNode =>
  React.isValidElement<HeaderElementProps>(header) ? (header.props.children ?? header) : header;

const InstructionComponent = () => {
  const { value: ifPayee } = useField('isEnabledPayeeSource');
  const { value: ifAmount } = useField('isEnabledAmountSource');

  return (
    <>
      {ifPayee ? (
        <>
          <Typography variant='paragraphRegular'>
            Now, add THEN conditions that you want your rule to change.
          </Typography>
          <TurnRightIcon sx={{ transform: 'rotate(90deg) scale(1, 1)' }} />
        </>
      ) : ifAmount ? (
        <Typography variant='paragraphRegular'>Add another IF condition to your rule</Typography>
      ) : (
        <>
          <TurnRightIcon sx={{ transform: 'rotate(90deg) scale(1, -1)' }} />
          <Typography variant='paragraphRegular'>Add IF conditions that will target transactions.</Typography>
        </>
      )}
    </>
  );
};

const SettingsDrawer: React.FC<CustomDrawerProps> = ({
  id,
  isOverlay = false,
  open,
  stage,
  onClose,
  onDismiss,
  onExited,
  header,
  children,
  footer,
  backText,
}) => {
  const { state } = useStateContext();
  const { rule } = state;
  const { classes } = useStyles();
  const useStandardDrawer = useAppSelector((reduxState) =>
    Boolean(featureFlagsSelectors.getFeatureFlag(reduxState, 'webDrawerInput')),
  );
  const isInStandardDrawer = useStandardDrawerVariant() !== null;
  const shouldRenderAsOverlay = isOverlay || isInStandardDrawer;
  const standardDrawerWidthSx = {
    width: {
      xs: '100vw',
      sm: RULES_V3_DRAWER_WIDTH,
    },
  };
  const standardDrawerPaperSx = {
    ...standardDrawerWidthSx,
  };
  const handleDismiss = onDismiss ?? onClose;
  const handleBackKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === 'Enter') {
      onClose();
    }
  };
  const standardHeaderTitle = getHeaderTitleContent(header);
  const headerContent = useStandardDrawer ? (
    <>
      <Box className={classes.headerTitle}>
        <IconButton
          aria-label='Back'
          id='dlg-back'
          onClick={onClose}
          onKeyDown={handleBackKeyDown}
          size='small'
          variant='tonal'
        >
          <ArrowBackRoundedIcon sx={{ fontSize: 30 }} />
        </IconButton>
        <Typography align='left' component='h2' variant='titleMedium' sx={{ fontWeight: 600, lineHeight: '24px' }}>
          {standardHeaderTitle}
        </Typography>
      </Box>
      <IconButton aria-label='Close' id='dlg-close' onClick={handleDismiss} size='small' variant='tonal'>
        <CloseRoundedIcon sx={{ fontSize: 30 }} />
      </IconButton>
    </>
  ) : (
    <>
      {header}
      <Box className={classes.headerActionSpacer} />
    </>
  );

  const content = (
    <Box className={classes.container}>
      <Box className={classes.header}>{headerContent}</Box>
      <Box className={classes.content}>
        <Box className={classes.headerRow}>
          {useStandardDrawer ? (
            <Box className={classes.spacer} />
          ) : (
            <Button
              className={classes.backNav}
              onClick={onClose}
              id='settings-drawer-backnav'
              startIcon={<ArrowBackIcon />}
            >
              <Typography className={classes.backNavText} variant='bodyRegular'>
                {backText}
              </Typography>
            </Button>
          )}
          {stage === 'VALIDATED' ? (
            <Box className={classes.spacer}></Box>
          ) : (
            rule &&
            !rule.id && (
              <Box className={classes.instructions}>
                <InstructionComponent />
              </Box>
            )
          )}
          <Box className={classes.spacer}></Box>
        </Box>
        {children}
      </Box>
      <Box className={classes.footer}>{footer}</Box>
    </Box>
  );

  // When the drawer is open, render a fixed shield over the nav bar area
  // (left 75px) at a z-index above the backdrop. This absorbs pointer events
  // so clicks there don't reach the backdrop and accidentally close the drawer.
  const navShield = open && (
    <Portal>
      <Box
        aria-hidden
        data-testid='nav-bar-shield'
        sx={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          width: '75px',
          zIndex: (theme) => theme.zIndex.modal + 1,
          cursor: 'default',
        }}
      />
    </Portal>
  );

  if (useStandardDrawer) {
    return (
      <>
        {navShield}
        <StandardDrawer
          id={id}
          open={open}
          onClose={onClose}
          onExited={onExited}
          showScrim={!shouldRenderAsOverlay}
          variant={shouldRenderAsOverlay ? 'overlay' : 'temporary'}
          width='XLarge'
          slotProps={{
            paper: {
              className: classes.standardDrawerPaper,
              sx: standardDrawerPaperSx,
            },
          }}
        >
          {content}
        </StandardDrawer>
      </>
    );
  }

  return (
    <>
      {navShield}
      <Drawer
        id={id}
        className={classes.drawer}
        anchor='right'
        open={open}
        onClose={onClose}
        classes={{
          paper: classes.drawerPaper,
        }}
        sx={getDrawerSx({ zIndex: (theme) => theme.zIndex.modal })}
        slotProps={{
          transition: {
            onExited,
          },
        }}
      >
        {content}
      </Drawer>
    </>
  );
};

export default SettingsDrawer;
